import { Button } from '@components/Button';
import ShopCard from '@components/Card/ShopCard';
import CardGridWithMobileCarousel from '@components/CardGridWithMobileCarousel';
import { CarouselHeadline } from '@components/CardGridWithMobileCarousel/style';
import CardGridWithPagination from '@components/CardGridWithPagination';
import Block from '@components/Layout/Block';
import { VerticalLineSeparator } from '@components/MiscComponents/VerticalLineSeparator';
import evAPI from '@util/api/ev';
import {
  generateResults as shopGenerateResults,
  mapResults as shopMapResult,
} from '@util/api/listingShops';
import { getTestId } from '@util/getTestIds';
import { useMediaQuery } from '@util/mediaQuery/index';
import { useTranslations } from 'next-intl';
import { FC, useEffect, useState } from 'react';
import { OddNumberCardHeadline, OddNumberCardInner, OddNumberCardWrapper } from './style';

// ** NOTE: This is a unique implementation of the CardGrid, uses a different type based on size of cardArr

interface OddNumberCardProps {
  ctaLink: string;
}

// An OddNumberCard component for group pages that don't have a number of cards divisible by 3
const OddNumberCard: FC<OddNumberCardProps> = ({ ctaLink }) => {
  const t = useTranslations();
  return (
    <OddNumberCardWrapper>
      <OddNumberCardInner>
        <OddNumberCardHeadline data-testid="local-shop-desc">
          {t('Each shop adds more to our story')}
        </OddNumberCardHeadline>
        <Button
          dataTestId="local-shop-learn-more-btn"
          variant="tertiary"
          label={t('Learn more about us')}
          url={ctaLink}
          arrow={true}
          arrowColor="black"
        />
      </OddNumberCardInner>
    </OddNumberCardWrapper>
  );
};

interface ShopGroupCarouselProps {
  ourLocalShopsHeadline?: string;
  shopIds: string;
}

const ShopGroupCarousel: FC<ShopGroupCarouselProps> = ({ ourLocalShopsHeadline, shopIds }) => {
  const t = useTranslations();
  const header = ourLocalShopsHeadline ?? t('Our Local Shops');
  const api = new evAPI();

  const [cardArr, setCardArr] = useState<any[]>([]);

  const getShops = async (shopIds) => {
    try {
      const results = await shopGenerateResults({
        sortType: {
          sortKey: 'EvaId',
          sortDir: 'asc',
        },
        enhancementFilter: {
          EvaId: { ne: null },
        },
        offset: 0,
        limit: 100,
        api,
        shops: shopIds,
      });
      const mapped = await shopMapResult(results.records);
      setCardArr(mapped);
    } catch (error) {
      console.error(error);
      setCardArr([]);
    }
  };

  useEffect(() => {
    if (shopIds) {
      getShops(shopIds);
    }
  }, [shopIds]);

  let mappedCardArr = cardArr.map((card, ind) => {
    return (
      <ShopCard
        key={ind.toString()}
        dataTestId={`local-shop-card-${ind}`}
        image={card.image}
        title={card.title}
        phone={card.phone}
        address={card.address}
        breNumber={card.breNumber}
        urlAdvisors={card.urlAdvisors}
        urlContactUs={card.urlContactUs}
        urlWebsite={card.urlWebsite}
        urlFacebook={card.urlFacebook}
        urlTwitter={card.urlTwitter}
        urlLinkedin={card.urlLinkedin}
        urlInstagram={card.urlInstagram}
        urlTiktok={card.urlTiktok}
        urlYoutube={card.urlYoutube}
        isShopGroupHomePage={true}
        imageAspectRatio={card.imageAspectRatio}
      />
    );
  });

  //
  //
  // Determine whether or not to add OddNumberCard component to end of cardArr
  //
  let pageIsBiggerThanMobile = useMediaQuery('(min-width: 600px) and (max-width: 900px)');
  let pageIsBiggerThanTablet = useMediaQuery('(min-width: 1440px)');

  let numberOfCards = cardArr.length;
  let numberOfCardsIsDivisibleByThree = numberOfCards % 3 === 0;
  let numberOfCardsIsDivisibleByTwo = numberOfCards % 2 === 0;

  if (
    (pageIsBiggerThanMobile && !numberOfCardsIsDivisibleByTwo) ||
    (pageIsBiggerThanTablet && !numberOfCardsIsDivisibleByThree)
  ) {
    // add OddNumberCard component to end of cardArr
    mappedCardArr.push(
      <OddNumberCard ctaLink={'/our-shops'} key={(numberOfCards + 1).toString()} />,
    );
  }

  const numberOfResultsToSwitchToPagination = 18;

  return (
    <Block>
      <CarouselHeadline data-testid={`${getTestId(header)}-header`}>{header}</CarouselHeadline>

      <VerticalLineSeparator />

      {cardArr && numberOfCards > numberOfResultsToSwitchToPagination ? (
        <CardGridWithPagination
          cardsPerPage={numberOfResultsToSwitchToPagination}
          cardArr={mappedCardArr}
          testIdType="local-shop-paginated"
          cardType="shop"
        />
      ) : (
        <CardGridWithMobileCarousel
          dataTestId="local-shop-mobile"
          cardArr={mappedCardArr}
          cardType="shop"
        />
      )}
    </Block>
  );
};

export default ShopGroupCarousel;
